import React from "react";
import RosterPicker from "./RosterPicker";

/**
 * PlanBriefFields — the inputs the AI is allowed to use when drafting a plan.
 *
 * Shared by the standalone "Draft a lesson plan" page and the Calendar event
 * page, so the two cannot drift. Every field here is sent to /ai/lesson-plan
 * and asserted in backend/tests/test_plan_brief.py.
 */

const FOCUS_PRESETS = [
  "3rd-shot drops",
  "Dinking consistency at the kitchen",
  "Resetting from the transition zone",
  "Serve + return depth",
  "Stacking / poaching tactics",
  "Footwork + split-step timing",
];

function Label({ children, hint }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
      {children}
      {hint && <span className="normal-case tracking-normal text-white/40 font-normal"> {hint}</span>}
    </div>
  );
}

export default function PlanBriefFields({
  value = {},
  onChange,
  students = [],
  isGroup = false,
  showStudentPicker = true,
  showTitle = false,
}) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const setRaw = (k, v) => onChange({ ...value, [k]: v });
  const addPreset = (p) =>
    onChange({ ...value, focus: value.focus ? `${value.focus}\n• ${p}` : `• ${p}` });

  return (
    <div className="space-y-5" data-testid="plan-brief-fields">
      <div className="grid md:grid-cols-4 grid-cols-2 gap-3">
        <div>
          <Label>Duration (min)</Label>
          <input
            type="number" min="15" max="240"
            value={value.duration_mins ?? ""}
            onChange={set("duration_mins")}
            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
            data-testid="brief-duration"
          />
        </div>
        <div>
          <Label># Students</Label>
          <input
            type="number" min="1" max="20"
            value={isGroup ? (value.group_size ?? "") : 1}
            onChange={set("group_size")}
            disabled={!isGroup}
            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm disabled:opacity-50"
            data-testid="brief-group-size"
          />
        </div>
        <div>
          <Label>Courts</Label>
          <input
            type="number" min="1" max="20"
            value={value.courts ?? ""}
            onChange={set("courts")}
            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
            data-testid="brief-courts"
          />
        </div>
        <div>
          <Label hint="(blank = use roster)">Level / DUPR</Label>
          <input
            type="number" step="0.1" min="1.0" max="6.0"
            value={value.skill_level ?? ""}
            onChange={set("skill_level")}
            placeholder="3.5"
            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
            data-testid="brief-skill"
          />
        </div>
      </div>

      {showTitle && (
        <div>
          <Label>Title (optional)</Label>
          <input
            type="text"
            value={value.title ?? ""}
            onChange={set("title")}
            placeholder="e.g. Friday dinking clinic"
            className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
            data-testid="brief-title"
          />
        </div>
      )}

      {showStudentPicker && (
        <div>
          {isGroup ? (
            <>
              <Label hint="(blank = use the event roster)">Students in this plan</Label>
              <div className="mt-2">
                <RosterPicker
                  students={students}
                  selectedIds={value.student_ids || []}
                  onChange={(ids) => setRaw("student_ids", ids)}
                  title="Roster for this plan"
                />
              </div>
            </>
          ) : (
            <div>
              <Label>Student (optional)</Label>
              <select
                value={value.student_id || ""}
                onChange={set("student_id")}
                className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm mt-1"
                data-testid="brief-student-select"
              >
                <option value="" className="bg-[#141414]">No specific student (generic plan)</option>
                {students.map((s) => (
                  <option key={s.user_id} value={s.user_id} className="bg-[#141414]">
                    {s.name} · DUPR {s.skill_level ?? "—"}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <Label>Focus</Label>
          <button
            type="button"
            onClick={() => setRaw("focus", "")}
            className="text-[10px] text-[#38BDF8] hover:underline"
          >
            Clear
          </button>
        </div>
        <textarea
          rows={4}
          value={value.focus ?? ""}
          onChange={set("focus")}
          placeholder="What should this session emphasize? e.g. 'Build third-shot drop consistency under pace, then use it to reset in the transition zone. Watch for early backhand grip slip.'"
          className="w-full bg-transparent border border-white/15 focus:border-[#38BDF8] outline-none px-3 py-2 text-sm mt-1 resize-y"
          data-testid="brief-focus"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {FOCUS_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => addPreset(p)}
              className="text-[10px] px-2 py-1 border border-white/10 text-[#A3A3A3] hover:border-[#38BDF8] hover:text-[#38BDF8]"
            >
              + {p}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label hint="(injuries, equipment, weather, goals…)">Anything else the AI should know</Label>
        <textarea
          rows={3}
          value={value.notes ?? ""}
          onChange={set("notes")}
          placeholder="e.g. One player is recovering from a shoulder strain — no overheads. Only two ball carts. Focus on game situations in the last 15 minutes."
          className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none px-3 py-2 text-sm mt-1 resize-y"
          data-testid="brief-notes"
        />
      </div>
    </div>
  );
}
